import type { Msg } from "../../../types/chat";
import { applyDeleted, applyEdited, applyReactions, applySeen, normalizeBacklog } from "../messageList";

// Module copy of ChatLayout's pure key helpers (same pattern as useFavorites / useAndroidShare).
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);
const k = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

type MsgsByKey = Record<string, Msg[]>;

type Options = {
  /** The space and channel on screen. */
  currentVoidId: string;
  currentChannelId: string;
  /** The same, read at call time (a backlog can arrive just after a channel switch). */
  currentVoidIdRef: { current: string };
  currentChannelIdRef: { current: string };
  socket: { emit: (event: string, payload?: unknown) => unknown };
  setMsgsByKey: (updater: (old: MsgsByKey) => MsgsByKey) => void;
  /** Removes the signed-in user from a message's "seen by" list. */
  stripSelfSeen: (m: Msg) => Msg;
  /** The message being edited in the composer, if any. */
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  setEditText: (text: string) => void;
  // Used by onBacklog:
  /** How many messages the server sends per page. */
  pageSize: number;
  /** Id of the oldest loaded message per channel: the cursor for "load previous". */
  earliestMsgIdRef: { current: Record<string, string | null> };
  setHasMoreByChan: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  setLoadingMoreByChan: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  setUnread: (updater: (prev: Record<string, number>) => Record<string, number>) => void;
  setNotified: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  /** Whether this user shares read receipts, and whether they appear invisible. */
  readReceiptsEnabledRef: { current: boolean };
  selfInvisibleRef: { current: boolean };
};

/**
 * Socket handlers for messages already in a channel's list: someone read the channel (a read receipt), a
 * message was edited or deleted, its reaction counts changed, or the channel's backlog arrived. Each one
 * hands the list to the matching function in `messageList.ts`.
 *
 * Returns the handlers, built fresh each render (so each sees the current state); ChatLayout registers
 * them with the rest of the socket events. Moved out of ChatLayout with the same behaviour.
 */
export function useMessageSocketHandlers({
  currentVoidId,
  currentChannelId,
  currentVoidIdRef,
  currentChannelIdRef,
  socket,
  setMsgsByKey,
  stripSelfSeen,
  editingId,
  setEditingId,
  setEditText,
  pageSize,
  earliestMsgIdRef,
  setHasMoreByChan,
  setLoadingMoreByChan,
  setUnread,
  setNotified,
  readReceiptsEnabledRef,
  selfInvisibleRef,
}: Options) {
  const onSeen = ({ channelId, userId, name }: { channelId: string; messageId: string; userId: string; name?: string }) => {
    const fqChan = fq(currentVoidId, currentChannelId);
    if (channelId !== fqChan) return;
    const kk = k(currentVoidId, channelId);
    setMsgsByKey((old) => {
      const list = old[kk] ?? [];
      const next = applySeen(list, { userId, name }, stripSelfSeen);
      return next === list ? old : { ...old, [kk]: next };
    });
  };

  const onEdited = ({ channelId, messageId, content, updatedAt }: { channelId: string; messageId: string; content: string; updatedAt?: string }) => {
    const kk = k(currentVoidId, channelId);
    setMsgsByKey((old) => {
      const list = old[kk] ?? [];
      const next = applyEdited(list, { messageId, content, updatedAt });
      return next === list ? old : { ...old, [kk]: next };
    });
    if (editingId === messageId) { setEditingId(null); setEditText(""); }
  };

  const onDeleted = ({ channelId, messageId }: { channelId: string; messageId: string }) => {
    const kk = k(currentVoidId, channelId);
    setMsgsByKey((old) => {
      const list = old[kk] ?? [];
      return { ...old, [kk]: applyDeleted(list, messageId) };
    });
  };

  const onReactions = ({ channelId, messageId, reactions }: { channelId: string; messageId: string; reactions: Record<string, { count: number }> }) => {
    const kk = k(currentVoidId, channelId);
    setMsgsByKey((old) => {
      const list = old[kk] ?? [];
      const next = applyReactions(list, { messageId, reactions });
      return next === list ? old : { ...old, [kk]: next };
    });
  };

  // A channel's recent messages arrived (when it was opened).
  const onBacklog = ({ voidId, channelId, messages }: { voidId: string; channelId: string; messages: Msg[] }) => {
    const kk = k(voidId, channelId);
    const { sliced, earliestId, hasMore } = normalizeBacklog(messages, voidId, pageSize);
    earliestMsgIdRef.current[kk] = earliestId;
    setHasMoreByChan(prev => ({ ...prev, [kk]: hasMore }));
    setLoadingMoreByChan(prev => { const next = { ...prev }; delete next[kk]; return next; });
    setMsgsByKey((old) => ({ ...old, [kk]: sliced.map(stripSelfSeen) }));
    // We just opened/loaded this channel — clear its local unread badge right
    // away and advance the server read watermark. Doing it here (rather than
    // only in handleSelectChannel) covers mobile, where channel/DM opens go
    // through bare switchChannel and never hit the optimistic clear, so the
    // badge used to linger until the next server unread hydration. silent
    // honours the read-receipt/invisible privacy setting (suppresses the
    // public "seen" receipt) without skipping our own read tracking.
    //
    // Match against the live refs, NOT the currentVoidId/currentChannelId
    // captured in this closure: a backlog can be received by a handler whose
    // closure still holds the *previous* channel. When that happened the guard
    // failed, the read:up_to was never emitted, the watermark never advanced, and
    // the space's unread dot re-appeared on the next /unread hydration after the
    // user left the channel.
    if (voidId === currentVoidIdRef.current && channelId === fq(voidId, currentChannelIdRef.current)) {
      const fid = fq(voidId, channelId);
      setUnread((prev) => { if (!prev[fid]) return prev; const next = { ...prev }; delete next[fid]; return next; });
      setNotified((prev) => { if (!prev[fid]) return prev; const next = { ...prev }; delete next[fid]; return next; });
      const last = sliced[sliced.length - 1];
      if (last?.id) {
        const silent = !readReceiptsEnabledRef.current || selfInvisibleRef.current;
        socket.emit('read:up_to', { channelId: fid, lastMessageId: last.id, silent });
      }
    }
  };

  return { onSeen, onEdited, onDeleted, onReactions, onBacklog };
}

